import mammoth from "mammoth";
import TurndownService from "turndown";
import {gfm} from "turndown-plugin-gfm";
import {marked} from "marked";
import DOMPurify from "dompurify";
import api, {getAdminUploadURL} from "./api";
import {resolveUrl} from "./app-config";

export const WORD_FILE_LIMITS = Object.freeze({
    maxFileSize: 25 * 1024 * 1024,
    maxImages: 100,
    maxImageBytes: 40 * 1024 * 1024,
    allowedImageTypes: ["image/png", "image/jpeg", "image/gif", "image/webp"],
    uploadConcurrency: 3,
});

export const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const IMAGE_PLACEHOLDER_PREFIX = "__ZF_IMG_";

const DOCX_EXTENSION = /\.docx$/i;

export function isDocxFile(file) {
    const mime = (file?.type || "").toLowerCase();
    const name = (file?.name || "").toLowerCase();
    return DOCX_EXTENSION.test(name) || mime === DOCX_MIME;
}

export function validateWordFile(file) {
    if (!file) return {ok: false, error: "word-file-missing"};
    if (!isDocxFile(file)) return {ok: false, error: "word-file-type-not-allowed"};
    if (file.size > WORD_FILE_LIMITS.maxFileSize) return {ok: false, error: "word-file-too-large"};
    return {ok: true};
}

export function sanitizeFileName(name) {
    const base = String(name || "image")
        .replace(/[\\/:*?"<>|\u0000-\u001f]/g, "_")
        .trim();
    return base || "image";
}

function extensionForMime(contentType = "") {
    const map = {
        "image/png": "png",
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/gif": "gif",
        "image/webp": "webp",
        "image/bmp": "bmp",
        "image/svg+xml": "svg",
    };
    return map[String(contentType).toLowerCase()] || "png";
}

export function validateExtractedImages(images = []) {
    const errors = [];
    if (images.length > WORD_FILE_LIMITS.maxImages) errors.push("word-too-many-images");
    const totalBytes = images.reduce((sum, image) => sum + Number(image.size || 0), 0);
    if (totalBytes > WORD_FILE_LIMITS.maxImageBytes) errors.push("word-images-too-large");
    const unsupported = images.find((image) => !WORD_FILE_LIMITS.allowedImageTypes.includes(String(image.contentType || "").toLowerCase()));
    if (unsupported) errors.push("word-image-type-not-allowed");
    return {ok: errors.length === 0, errors};
}

export function looksLikeHtml(value) {
    if (!value) return false;
    return /<\/?[a-z][a-z0-9-]*\b[^>]*>/i.test(String(value));
}

export function looksLikeMarkdown(value) {
    return !looksLikeHtml(value);
}

const turndownService = (() => {
    const service = new TurndownService({
        headingStyle: "atx",
        bulletListMarker: "-",
        codeBlockStyle: "fenced",
        emDelimiter: "*",
        strongDelimiter: "**",
        hr: "---",
    });
    service.use(gfm);
    service.addRule("wordList", {
        filter: "li",
        replacement: (content, node, options) => {
            const cleaned = content.replace(/^\n+/, "").replace(/\n+$/, "\n").replace(/\n/gm, "\n    ");
            let prefix = `${options.bulletListMarker} `;
            if (node.parentNode?.nodeName === "OL") {
                const start = Number(node.parentNode.getAttribute("start") || 1);
                const index = Array.prototype.indexOf.call(node.parentNode.children, node);
                prefix = `${start + index}. `;
            }
            return `${prefix}${cleaned}${node.nextSibling && !/\n$/.test(cleaned) ? "\n" : ""}`;
        },
    });
    service.addRule("wordTable", {
        filter: "table",
        replacement(content, node) {
            const rows = [];
            node.querySelectorAll("tr")?.forEach((tr) => {
                const cells = Array.from(tr.querySelectorAll("th, td")).map((cell) => cellText(cell));
                if (cells.every((cell) => !cell)) return;
                rows.push(cells);
            });
            if (!rows.length) return "";

            const header = rows[0];
            const body = rows.slice(1).filter((row) => row.some((cell) => cell));
            const lines = [
                `| ${header.join(" | ")} |`,
                `| ${header.map(() => "---").join(" | ")} |`,
                ...body.map((row) => `| ${row.join(" | ")} |`),
            ];
            return `\n\n${lines.join("\n")}\n`;
        },
    });
    return service;
})();

function cellText(element) {
    let text = "";
    Array.from(element?.childNodes || []).forEach((child) => {
        if (child.nodeType === 3) {
            text += child.textContent || "";
        } else if (child.nodeName === "BR") {
            text += " ";
        } else {
            text += ` ${child.textContent || ""} `;
        }
    });
    return text.replace(/[ \t]+\n/g, "\n").replace(/\s+/g, " ").trim().replace(/\|/g, "\\|");
}

export function simplifyHtmlForMarkdown(html) {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(String(html), "text/html");

    doc.querySelectorAll("li p, td p, th p").forEach((p) => {
        const parent = p.parentNode;
        while (p.firstChild) parent.insertBefore(p.firstChild, p);
        parent.removeChild(p);
    });

    doc.querySelectorAll("p").forEach((node) => {
        const hasContent = node.textContent?.trim() || node.querySelector("img, br, table");
        if (!hasContent) node.parentNode?.removeChild(node);
    });

    return doc.body ? doc.body.innerHTML : String(html);
}

export function normalizeMarkdown(markdown) {
    return String(markdown || "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

export function htmlToMarkdown(html) {
    if (!html) return "";
    const clean = DOMPurify.sanitize(String(html));
    const simplified = simplifyHtmlForMarkdown(clean);
    return normalizeMarkdown(turndownService.turndown(simplified));
}

export function markdownToHtml(markdown) {
    if (!markdown) return "";
    const raw = marked.parse(String(markdown), {gfm: true});
    return DOMPurify.sanitize(raw);
}

export function stripUnsafeImageSources(markdown) {
    return String(markdown || "").replace(/\]\((?:blob|data):[^)]*\)/gi, "](#)");
}

export function imagePlaceholder(index) {
    return `${IMAGE_PLACEHOLDER_PREFIX}${index}__`;
}

export function replaceImagePlaceholders(markdown, imageUrls = []) {
    let output = String(markdown || "");
    imageUrls.forEach(({index, url}) => {
        if (typeof index !== "number" || !url) return;
        output = output.split(imagePlaceholder(index)).join(url);
    });
    return output;
}

export async function extractDocxContent(arrayBuffer, {onImage, signal} = {}) {
    const images = [];

    const result = await mammoth.convertToHtml(
        {arrayBuffer},
        {
            includeDefaultStyleMap: true,
            convertImage: mammoth.images.imgElement(async (image) => {
                if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
                const index = images.length;
                const bytes = await image.readAsArrayBuffer();
                const contentType = String(image.contentType || "image/png").toLowerCase();
                const extension = extensionForMime(contentType);
                const blob = new Blob([bytes], {type: contentType});
                images.push({
                    index,
                    fileName: sanitizeFileName(`word-image-${index + 1}.${extension}`),
                    contentType,
                    size: blob.size,
                    blob,
                });
                onImage?.(index + 1);
                return {src: imagePlaceholder(index)};
            }),
        }
    );

    return {html: result.value || "", messages: result.messages || [], images};
}

async function uploadOne(image, signal, uploader) {
    if (uploader) return {index: image.index, url: await uploader(image.blob, image.fileName, signal)};
    const formData = new FormData();
    formData.append("files", image.blob, image.fileName);
    formData.append("fileInfo", JSON.stringify({name: image.fileName}));
    const {data} = await api.post(getAdminUploadURL(), formData, {signal});
    const file = Array.isArray(data) ? data[0] : data;
    return {index: image.index, url: resolveUrl(file?.url)};
}

export async function uploadExtractedImages(images = [], {onProgress, concurrency = WORD_FILE_LIMITS.uploadConcurrency, signal, uploader} = {}) {
    if (images.length === 0) return [];
    const results = new Array(images.length);
    let cursor = 0;
    let done = 0;

    const worker = async () => {
        while (cursor < images.length) {
            const slot = cursor;
            cursor += 1;
            const image = images[slot];
            try {
                const uploaded = await uploadOne(image, signal, uploader);
                results[slot] = {index: image.index, url: uploaded.url, ok: true};
            } catch (error) {
                results[slot] = {index: image.index, ok: false, error};
            }
            done += 1;
            onProgress?.(done, images.length);
        }
    };

    const workerCount = Math.max(1, Math.min(concurrency, images.length));
    await Promise.all(Array.from({length: workerCount}, () => worker()));
    return results;
}

export async function convertWordFileToMarkdown(file, {onProgress, onImage, signal, maxUploadRetries = 2, uploader} = {}) {
    const validation = validateWordFile(file);
    if (!validation.ok) return {ok: false, error: validation.error};

    let arrayBuffer;
    try {
        arrayBuffer = await file.arrayBuffer();
    } catch (error) {
        if (signal?.aborted) return {ok: false, error: "word-import-cancelled", aborted: true};
        return {ok: false, error: "word-file-read-failed"};
    }

    if (arrayBuffer.byteLength > WORD_FILE_LIMITS.maxFileSize) {
        return {ok: false, error: "word-file-too-large"};
    }

    const {html, images} = await extractDocxContent(arrayBuffer, {onImage, signal});

    const imageValidation = validateExtractedImages(images);
    if (!imageValidation.ok) {
        return {ok: false, error: imageValidation.errors[0], details: imageValidation.errors};
    }

    const imageUrls = {};
    if (images.length > 0) {
        let remaining = images;
        let attempt = 0;
        while (remaining.length > 0 && attempt <= maxUploadRetries && !signal?.aborted) {
            const uploads = await uploadExtractedImages(remaining, {
                signal,
                uploader,
                onProgress: (done, total) => onProgress?.(Object.keys(imageUrls).length + done, images.length),
            });
            const failedIndexes = [];
            uploads.forEach((upload) => {
                if (upload.ok) {
                    imageUrls[upload.index] = upload.url;
                } else {
                    failedIndexes.push(upload.index);
                }
            });
            remaining = images.filter((image) => failedIndexes.includes(image.index));
            attempt += 1;
        }
        if (remaining.length > 0) {
            return {ok: false, error: "word-image-upload-failed", failed: remaining};
        }
    }

    const orderedUrls = Object.keys(imageUrls)
        .map(Number)
        .sort((a, b) => a - b)
        .map((index) => ({index, url: imageUrls[index]}));

    const markdown = htmlToMarkdown(html);
    const finalMarkdown = stripUnsafeImageSources(replaceImagePlaceholders(markdown, orderedUrls));
    return {ok: true, markdown: finalMarkdown, images: orderedUrls};
}
